import { useState, useRef, useEffect, useMemo } from 'react'
import { useAppContext } from '../../context/AppContext'
import { useQueryProcessing } from '../../hooks/useApi'
import { apiClient } from '../../services/apiClient'
import MessageBubble from './MessageBubble'
import WelcomeScreen from './WelcomeScreen'
import { getVisibleSources } from '../../config/sourcesConfig'
import './ChatInterface.css'

// Helper function to get available models
// All models are always available - API keys are managed on the server
const getAvailableModelsFromState = (_state) => {
  const providers = _state?.systemProviders || {}
  const providerIds = ['openai', 'anthropic']

  return providerIds.flatMap((providerId) => {
    const configuredPrimary = _state?.agentModels?.[providerId]
    const providerModels = providers?.[providerId]?.models || []
    const mergedModels = Array.from(new Set([...providerModels, configuredPrimary].filter(Boolean)))

    return mergedModels.map((model) => ({
      provider: providerId,
      model,
      name: model
    }))
  })
}

const ChatInterface = () => {
  const { state, dispatch, actions, defaultModelFor } = useAppContext()
  // Note: Permission checks handled by backend, errors shown via toast automatically
  const { processQuery, loading } = useQueryProcessing()
  // Don't destructure to preserve 'this' context
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [agentSteps, setAgentSteps] = useState([])
  const agentStepsRef = useRef([])
  const [abortController, setAbortController] = useState(null)
  const [newMessageIds, setNewMessageIds] = useState(new Set())
  const [extendingModel, setExtendingModel] = useState(null)
  const [, setUseInternalKnowledge] = useState(false)
  const [showSourcesDropdown, setShowSourcesDropdown] = useState(false)
  const [enabledSources, setEnabledSources] = useState({})
  const [personalContextEnabled, setPersonalContextEnabled] = useState(() => {
    const saved = localStorage.getItem('omnicontext_personal_context_enabled')
    return saved !== null ? JSON.parse(saved) : true
  })
  const [agenticMode, setAgenticMode] = useState(() => {
    return localStorage.getItem('omnicontext_agentic_mode') === 'true'
  })
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)

  // Use global chat messages from context (memoized to keep hook deps stable)
  const messages = useMemo(() => state.chatMessages || [], [state.chatMessages])
  const [isInitialized, setIsInitialized] = useState(false)

  // Define available information sources (filtered by visibility) as state
  // Exclude personal_context as it has its own dedicated toggle
  const [informationSources, setInformationSources] = useState(() =>
    getVisibleSources()
      .filter(source => source.id !== 'personal_context')
      .map(source => ({
        id: source.id,
        name: source.name,
        icon: source.icon,
        description: source.description,
        isConfigured: false // Will be dynamically checked from backend
      }))
  )

  // Initialize chat interface: load settings and ensure session exists
  useEffect(() => {
    const initializeChatInterface = async () => {
      if (isInitialized) return

      console.log('🚀 Initializing ChatInterface...')

      // Load enabled sources from localStorage
      const savedEnabledSources = localStorage.getItem('enabledSources')
      if (savedEnabledSources) {
        try {
          setEnabledSources(JSON.parse(savedEnabledSources))
        } catch (e) {
          console.error('Failed to parse enabled sources from localStorage:', e)
        }
      }

      // Also check RAG enabled state
      try {
        const config = await apiClient.getRagIntegrationConfig()
        const configData = config?.config || config
        if (configData && typeof configData.enabled === 'boolean') {
          const isRAGEnabled = configData.enabled
          localStorage.setItem('ragEnabled', JSON.stringify(isRAGEnabled))
          setUseInternalKnowledge(isRAGEnabled)
          setEnabledSources(prev => ({
            ...prev,
            internal_kb: isRAGEnabled
          }))
        }
      } catch (error) {
        const ragEnabledFromStorage = localStorage.getItem('ragEnabled')
        if (ragEnabledFromStorage) {
          const isRAGEnabled = JSON.parse(ragEnabledFromStorage)
          setUseInternalKnowledge(isRAGEnabled)
          setEnabledSources(prev => ({
            ...prev,
            internal_kb: isRAGEnabled
          }))
        }
      }

      // Internal KB (RAG) can always be toggled from the dropdown; configuration is persisted server-side.
      setInformationSources(prev => prev.map(s =>
        s.id === 'internal_kb' ? { ...s, isConfigured: true } : s
      ))

      // Check Bedrock configuration status
      try {
        const config = await apiClient.request('/bedrock/config', { method: 'GET' })
        if (config && config.is_configured) {
          console.log('Bedrock is configured, updating information sources')
          setInformationSources(prev => prev.map(s =>
            s.id === 'bedrock_kb' ? { ...s, isConfigured: true } : s
          ))
        }
      } catch (error) {
        console.error('Error checking Bedrock configuration:', error)
      }

      // Load Clabs configuration and sync enabled state
      try {
        const clabsResponse = await apiClient.getClabsConfig()
        console.log('📦 Clabs config loaded:', clabsResponse)

        // Handle both nested (clabsResponse.config) and direct response formats
        const configData = clabsResponse?.config || clabsResponse

        if (configData && (configData.id || configData.api_url)) {
          console.log('📦 Clabs config data:', configData)

          // Sync the clabs_source enabled state from backend config
          const clabsEnabled = configData.enabled !== undefined ? configData.enabled : true
          console.log('🔧 Setting clabs_source enabled to:', clabsEnabled)
          setEnabledSources(prev => ({
            ...prev,
            clabs_source: clabsEnabled
          }))

          // Mark Clabs source as configured if we have a valid config
          setInformationSources(prev => prev.map(s =>
            s.id === 'clabs_source' ? { ...s, isConfigured: true } : s
          ))
        }
      } catch (error) {
        console.log('ℹ️ No Clabs configuration found or error loading:', error.message)
      }

      // Load Web Search configuration and sync enabled state
      try {
        const webSearchConfig = await apiClient.getWebSearchConfig()
        if (webSearchConfig) {
          const webSearchEnabled = !!webSearchConfig.enabled
          setEnabledSources(prev => ({
            ...prev,
            web_search: webSearchEnabled
          }))
          setInformationSources(prev => prev.map(s =>
            s.id === 'web_search' ? { ...s, isConfigured: true } : s
          ))
        }
      } catch (error) {
        console.log('ℹ️ No Web Search configuration found or error loading:', error.message)
      }

      // Initialize session: if no current session, try to load the most recent one
      if (!state.currentSessionId) {
        try {
          console.log('📋 No current session, attempting to load most recent session...')
          const response = await apiClient.getChatSessions(1) // Get most recent session

          if (response.sessions && response.sessions.length > 0) {
            const recentSession = response.sessions[0]
            console.log('✅ Found recent session:', recentSession.sessionId)
            dispatch({ type: actions.SET_CURRENT_SESSION, payload: recentSession.sessionId })

            // The session messages will be loaded by the other useEffect that watches currentSessionId
          } else {
            console.log('ℹ️ No existing sessions found, will create new session on first message')
          }
        } catch (error) {
          console.error('❌ Error loading recent sessions:', error)
          // Not a critical error - session will be created on first message
        }
      }

      setIsInitialized(true)
      console.log('✅ ChatInterface initialized')
    }

    initializeChatInterface()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // Save personal context preference to localStorage
  useEffect(() => {
    localStorage.setItem('omnicontext_personal_context_enabled', JSON.stringify(personalContextEnabled))
  }, [personalContextEnabled])

  // Handle toggling a source
  const toggleSource = async (sourceId) => {
    // Backend automatically handles permission checks via apiClient
    const newEnabledState = !enabledSources[sourceId]

    // Optimistically update UI
    setEnabledSources(prev => {
      const newState = {
        ...prev,
        [sourceId]: newEnabledState
      }
      // Save to localStorage
      localStorage.setItem('enabledSources', JSON.stringify(newState))
      return newState
    })

    // If it's the internal KB, also update the RAG state
    if (sourceId === 'internal_kb') {
      setUseInternalKnowledge(newEnabledState)
      localStorage.setItem('ragEnabled', JSON.stringify(newEnabledState))
      try {
        await apiClient.updateRagEnabled(newEnabledState)
      } catch (error) {
        console.error('❌ Error updating RAG enabled state:', error)
        // Revert UI on error
        setEnabledSources(prev => ({
          ...prev,
          [sourceId]: !newEnabledState
        }))
        setUseInternalKnowledge(!newEnabledState)
        localStorage.setItem('ragEnabled', JSON.stringify(!newEnabledState))
      }
    }

    // If it's the Clabs source, sync with backend
    if (sourceId === 'clabs_source') {
      try {
        console.log('🔄 Updating Clabs enabled state to:', newEnabledState)
        const result = await apiClient.updateClabsEnabled(newEnabledState)
        if (result && result.success) {
          console.log('✅ Clabs enabled state updated successfully')
        } else {
          console.error('❌ Failed to update Clabs enabled state:', result?.message)
          // Revert UI on failure
          setEnabledSources(prev => ({
            ...prev,
            [sourceId]: !newEnabledState
          }))
        }
      } catch (error) {
        console.error('❌ Error updating Clabs enabled state:', error)
        // Revert UI on error
        setEnabledSources(prev => ({
          ...prev,
          [sourceId]: !newEnabledState
        }))
      }
    }

    if (sourceId === 'web_search') {
      try {
        await apiClient.updateWebSearchConfig({ enabled: newEnabledState })
      } catch (error) {
        console.error('❌ Error updating Web Search enabled state:', error)
        setEnabledSources(prev => ({
          ...prev,
          [sourceId]: !newEnabledState
        }))
      }
    }
  }

  // Sync agentic mode with localStorage and listen for changes from Organization tab
  useEffect(() => {
    localStorage.setItem('omnicontext_agentic_mode', String(agenticMode))
  }, [agenticMode])

  useEffect(() => {
    const handler = (e) => setAgenticMode(e.detail.enabled)
    window.addEventListener('agenticModeChanged', handler)
    return () => window.removeEventListener('agenticModeChanged', handler)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSourcesDropdown(false)
      }
    }

    if (showSourcesDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSourcesDropdown])

  // Debug: Monitor loading state changes
  useEffect(() => {
    console.log('Loading state changed:', loading)
  }, [loading])

  // Debug: Add global diagnostic function
  useEffect(() => {
    window.debugChatInterface = () => {
      console.log('=== Chat Interface Debug Info ===')
      console.log('inputValue:', inputValue)
      console.log('inputValue.trim():', inputValue.trim())
      console.log('loading:', loading)
      console.log('isTyping:', isTyping)
      console.log('abortController:', abortController)
      console.log('Button disabled:', !inputValue.trim() || loading)
      console.log('messages count:', messages.length)
      console.log('currentSessionId:', state.currentSessionId)
      console.log('=================================')
    }

    return () => {
      delete window.debugChatInterface
    }
  }, [inputValue, loading, isTyping, abortController, messages.length, state.currentSessionId])

  // Listen for example query selection from WelcomeScreen
  useEffect(() => {
    const handleExampleSelect = (event) => {
      const query = event.detail
      console.log('Example query selected:', query)
      setInputValue(query)
      dispatch({ type: actions.SET_USER_QUERY, payload: query })

      // Focus the input field
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    window.addEventListener('selectExample', handleExampleSelect)

    return () => {
      window.removeEventListener('selectExample', handleExampleSelect)
    }
  }, [dispatch, actions])

  // Create a new session if none exists
  const ensureSession = async (queryText = "", workspaceId = null) => {
    const title = queryText.trim()
      ? queryText.substring(0, 30) + (queryText.length > 30 ? '...' : '')
      : "New Chat"

    if (!state.currentSessionId) {
      try {
        console.log('Creating new session for workspace:', workspaceId)
        const response = await apiClient.createChatSession(title, workspaceId)
        const sessionId = response.session.sessionId || response.session._id || response.session.id
        dispatch({ type: actions.SET_CURRENT_SESSION, payload: sessionId })
        window.dispatchEvent(new Event('chatSessionsUpdated'))
        return sessionId
      } catch (error) {
        console.error('Error creating new session:', error)
        return null
      }
    }

    // Verify that the current session exists in the backend
    try {
      console.log('Verifying session exists:', state.currentSessionId)
      await apiClient.getChatSession(state.currentSessionId)
      console.log('Session verified successfully')
      return state.currentSessionId
    } catch (error) {
      // Session doesn't exist in backend - this is normal, just create a new one
      console.log('Session not found in backend, creating new session...')
      try {
        const response = await apiClient.createChatSession(title, workspaceId)
        const sessionId = response.session.sessionId || response.session._id || response.session.id
        dispatch({ type: actions.SET_CURRENT_SESSION, payload: sessionId })
        dispatch({ type: actions.CLEAR_CURRENT_CHAT }) // Clear old messages
        window.dispatchEvent(new Event('chatSessionsUpdated'))
        return sessionId
      } catch (createError) {
        console.error('Error creating new session:', createError)
        return null
      }
    }
  }

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, extendingModel])

  // Sync inputValue with global userQuery state (for example queries)
  useEffect(() => {
    if (state.userQuery && state.userQuery !== inputValue) {
      setInputValue(state.userQuery)
      // Auto-resize when value is set programmatically
      if (inputRef.current) {
        const textarea = inputRef.current
        textarea.style.height = 'auto'
        const minHeight = 44
        const maxHeight = 200
        const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight))
        textarea.style.height = `${newHeight}px`
      }
    }
  }, [state.userQuery, inputValue])

  // Load messages when session changes or on mount
  useEffect(() => {
    const loadSessionMessages = async () => {
      if (!state.currentSessionId) {
        console.log('No current session, skipping message load')
        return
      }

      try {
        console.log('📥 Loading messages for session:', state.currentSessionId)
        const response = await apiClient.getChatSession(state.currentSessionId)

        if (response.session && response.session.messages) {
          console.log('✅ Loaded', response.session.messages.length, 'messages from session')
          dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: response.session.messages })
        } else {
          console.log('ℹ️ No messages found in session')
          dispatch({ type: actions.LOAD_CHAT_MESSAGES, payload: [] })
        }
      } catch (error) {
        // If session doesn't exist, that's okay - it will be created on first message
        if (error.message && error.message.includes('Session not found')) {
          console.log('ℹ️ Session not found in backend (will be created on first message)')
          dispatch({ type: actions.CLEAR_CURRENT_CHAT })
        } else {
          console.error('❌ Error loading session messages:', error)
        }
      }
    }

    loadSessionMessages()
    // Note: actions is removed from dependencies to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentSessionId, dispatch])

  // Clear new message IDs when session changes (loading existing messages)
  useEffect(() => {
    setNewMessageIds(new Set())
  }, [state.currentSessionId])

  // Clean up new message IDs after typing completes (with delay)
  useEffect(() => {
    if (newMessageIds.size > 0) {
      const timer = setTimeout(() => {
        setNewMessageIds(new Set())
      }, 5000) // Clear after 5 seconds to ensure typing is complete

      return () => clearTimeout(timer)
    }
  }, [newMessageIds])

  // Save message to current chat session
  const saveMessageToSession = async (message, sessionId = null) => {
    try {
      const targetSessionId = sessionId || state.currentSessionId
      if (targetSessionId) {
        console.log('Saving message to session:', targetSessionId)
        // Save to database
        await apiClient.addMessageToSession(targetSessionId, message)

        // Update session title if it's the first user message
        if (message.type === 'user' && messages.length === 0) {
          const title = message.content.substring(0, 30) + (message.content.length > 30 ? '...' : '')
          await apiClient.updateChatSession(targetSessionId, { title })
        }
      } else {
        console.warn('No session ID available for saving message')
      }
    } catch (error) {
      console.error('Error saving message to session:', error)
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('handleSubmit called:', {
      inputValue: inputValue,
      inputTrimmed: inputValue.trim(),
      loading: loading,
      shouldReturn: !inputValue.trim() || loading
    })
    if (!inputValue.trim() || loading) return

    const activeWorkspaceId = state.activeWorkspace?._id || state.activeWorkspace?.id || null

    // Ensure we have a session (errors handled automatically by apiClient)
    const sessionId = await ensureSession(inputValue.trim(), activeWorkspaceId)
    if (!sessionId) {
      // Session creation failed - error already shown by apiClient
      return
    }

    const userMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    }
    const assistantId = `assistant-${Date.now() + 1}`
    const optimisticAssistantMessage = {
      id: assistantId,
      type: 'assistant',
      content: '',
      timestamp: new Date()
    }

    // Mark this message as new
    setNewMessageIds(prev => new Set([...prev, userMessage.id, assistantId]))

    // Add user message to global state and clear input
    const updatedMessages = [...messages, userMessage]
    const messagesWithOptimistic = [...updatedMessages, optimisticAssistantMessage]
    dispatch({ type: actions.SET_CHAT_MESSAGES, payload: messagesWithOptimistic })
    setInputValue('')
    // Also clear the global userQuery state to remove it from UserPanel inbox
    dispatch({ type: actions.SET_USER_QUERY, payload: '' })
    setIsTyping(true)
    setAgentSteps([])
    agentStepsRef.current = []

    // Save to current session
    await saveMessageToSession(userMessage, sessionId)

    try {

      // Create new AbortController for this request
      const controller = new AbortController()
      setAbortController(controller)

      let response

      // Query processing - supervisor handles RAG internally as a context provider
      // No need to query RAG in frontend when supervisor is configured
      {
        // Determine query processing method
        const shouldUseSupervisor = state.supervisorLLM && state.contextEnabled
        const shouldUseSupervisorRouting = shouldUseSupervisor && state.autoModeEnabled && state.autoModeManagedBySupervisor

        // Debug logging to see why routing might not be activated
        console.log('\nDEBUGGING SUPERVISOR ROUTING CONDITIONS:')
        console.log('State values:')
        console.log('   • supervisorLLM:', !!state.supervisorLLM, state.supervisorLLM)
        console.log('   • contextEnabled:', state.contextEnabled)
        console.log('   • autoModeEnabled:', state.autoModeEnabled)
        console.log('   • autoModeManagedBySupervisor:', state.autoModeManagedBySupervisor)
        console.log('🔧 Calculated conditions:')
        console.log('   • shouldUseSupervisor:', shouldUseSupervisor)
        console.log('   • shouldUseSupervisorRouting:', shouldUseSupervisorRouting)

        if (shouldUseSupervisorRouting) {
          // Use Supervisor's intelligent routing for Auto Mode
          const availableModels = getAvailableModelsFromState(state)

          console.log('\n🧠 SUPERVISOR ROUTING ACTIVATED')
          console.log('Query:', userMessage.content)
          console.log('🔧 Supervisor config:', state.supervisorLLM)
          console.log('Available models:', availableModels)
          console.log('⚙️ Auto Mode settings:', {
            enabled: state.autoModeEnabled,
            managedBySupervisor: state.autoModeManagedBySupervisor
          })

          response = await apiClient.routeQueryWithSupervisor(
            userMessage.content,
            state.supervisorLLM,
            availableModels,
            controller.signal,
            activeWorkspaceId
          )
        } else if (shouldUseSupervisor) {
          // Use standard Supervisor processing
          response = await apiClient.streamSupervisorQuery(
            userMessage.content,
            state.supervisorLLM,
            state.contextEnabled,
            sessionId,
            personalContextEnabled,
            agenticMode,
            (data) => {
              if (data && data.type !== 'result') {
                setAgentSteps(prev => {
                  if (prev.length > 0 && prev[prev.length - 1].stage === data.stage) {
                    return prev
                  }
                  const next = [...prev, data]
                  agentStepsRef.current = next
                  return next
                })
              }
            },
            controller.signal,
            activeWorkspaceId
          )
        } else {
          // Use standard query processing
          response = await processQuery(userMessage.content, state.contextEnabled, controller.signal, activeWorkspaceId)
        }
      }

      // Simulate typing delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000))

      // Validate response
      if (!response) {
        throw new Error('Received empty response from server')
      }

      // Debug logging for supervisor response
      console.log('Supervisor response received:', response)
      console.log('supervisorUsed value:', response?.supervisorUsed)
      console.log('contextRetrieved value:', response.contextRetrieved)
      
      const rawAnswer = response.finalAnswer || response.response || 'I apologize, but I couldn\'t generate a response.'
      const aiMessage = {
        id: assistantId,
        type: 'assistant',
        content: rawAnswer,
        timestamp: new Date(),
        metadata: {
          agentSteps: agentStepsRef.current,
          processingTime: response.processingTime,
          agentsUsed: response.agentsUsed,
          contextRetrieved: response.contextRetrieved,
          supervisorUsed: response.supervisorUsed,
          contextSources: response.context_sources || response.contextSources || [],
          contextEntriesUsed: response.contextEntriesUsed || response.context_entries_found || 0,
          memoryId: response.memoryId || null,
          originalQuery: userMessage.content,
          needsExtension: response.needsExtension || false, // Flag from backend when context is insufficient
          // Supervisor routing information
          processingMethod: response.processingMethod || 'standard',
          routingAnalysis: response.routingAnalysis || null,
          supervisorModel: response.supervisorModel || null,  // ✅ Supervisor that analyzed
          selectedModel: response.selectedModel || null,      // ✅ Agent that generated response
          supervisorRouting: state.autoModeEnabled && state.autoModeManagedBySupervisor,
          // RAG information
          ragUsed: response.ragUsed || false,
          ragResults: response.ragResults || 0,
          ragQueryTime: response.ragQueryTime || 0
        }
      }

      console.log('AI message created with metadata:', aiMessage.metadata)
      const finalMessages = messagesWithOptimistic.map((message) =>
        message.id === assistantId ? aiMessage : message
      )
      dispatch({ type: actions.SET_CHAT_MESSAGES, payload: finalMessages })
      await saveMessageToSession(aiMessage, sessionId)
    } catch (error) {
      console.error('Query processing failed:', error)
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      })

      // Check if it was aborted
      if (error.name === 'AbortError') {
        const abortMessage = {
          id: assistantId,
          type: 'assistant',
          content: 'Response was stopped by user.',
          timestamp: new Date(),
          isError: false,
          metadata: {
            agentSteps: agentStepsRef.current
          }
        }
        const finalMessages = messagesWithOptimistic.map((message) =>
          message.id === assistantId ? abortMessage : message
        )
        dispatch({ type: actions.SET_CHAT_MESSAGES, payload: finalMessages })
        await saveMessageToSession(abortMessage, sessionId)
      } else {
        // Permission/auth errors already handled by apiClient
        // For other errors, show generic message in chat
        let errorContent = 'I apologize, but I encountered an error while processing your request.'

        if (error.message && !error.message.match(/^(403|401):/)) {
          // Only show error details if not a permission/auth error
          errorContent += `\n\n**Error details:** ${error.message}`
        }

        errorContent += '\n\n**Suggestion:** Please try again or contact support if the issue persists.'

        const errorMessage = {
          id: assistantId,
          type: 'assistant',
          content: errorContent,
          timestamp: new Date(),
          isError: true,
          metadata: {
            agentSteps: agentStepsRef.current
          }
        }
        const finalMessages = messagesWithOptimistic.map((message) =>
          message.id === assistantId ? errorMessage : message
        )
        dispatch({ type: actions.SET_CHAT_MESSAGES, payload: finalMessages })
        await saveMessageToSession(errorMessage, sessionId)
      }
    } finally {
      setIsTyping(false)
      setAgentSteps([])
      agentStepsRef.current = []
      setAbortController(null)
    }
  }


  // Function to stop the current response
  const handleStopResponse = () => {
    if (abortController) {
      abortController.abort()
      setAbortController(null)
      setIsTyping(false)
      setAgentSteps([])
      agentStepsRef.current = []
    }
  }

  // Handle input changes with auto-resize
  const handleInputChange = (e) => {
    const value = e.target.value
    setInputValue(value)

    // Also update the global state for consistency
    dispatch({ type: actions.SET_USER_QUERY, payload: value })

    // Auto-resize textarea with better limits
    const textarea = e.target
    textarea.style.height = 'auto'

    // Calculate new height with better limits
    const minHeight = 44 // Minimum height for single line
    const maxHeight = 200 // Maximum height (about 8-10 lines)
    const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight))

    textarea.style.height = `${newHeight}px`
  }

  // Handle Enter key (submit) and Shift+Enter (new line)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleExtendQuery = async (originalQuery, selectedLLM = null, originalMemoryId = null) => {
    try {
      console.log('Starting LLM extension process...')
      console.log('Original query:', originalQuery)
      console.log('Selected LLM:', selectedLLM)
      console.log('Original memory ID:', originalMemoryId)
      const activeWorkspaceId = state.activeWorkspace?._id || state.activeWorkspace?.id || null

      const summaryConfig = state.summaryConfig || {
        provider: 'ollama',
        model: defaultModelFor('ollama', 'classifier') || defaultModelFor('ollama', 'chat') || ''
      } // default when Organization LLM summarization not configured

      // Get supervisor configuration - Use selected LLM or default from AppContext
      let supervisorConfig = state.supervisorLLM || { provider: 'openai', model: defaultModelFor('openai', 'chat') || '' }

      if (selectedLLM) {
        // Comparison option uses both OpenAI and Anthropic providers
        if (selectedLLM.id === 'comparison') {
          const comparisonLLMs = (selectedLLM.llms || []).filter((llm) => !llm.summary)
          supervisorConfig = comparisonLLMs.map((llm) => ({
            provider: llm.provider || llm.id,
            model: llm.model
          }))
          console.log('Using comparison LLM config:', supervisorConfig)
        } else {
          // Use selected single LLM instead of default supervisor config
          supervisorConfig = {
            provider: selectedLLM.id,
            model: selectedLLM.model
          }
          console.log('Using selected LLM:', selectedLLM.name, selectedLLM.model)
        }
      } else {
        console.log('Using default supervisor config:', supervisorConfig)
      }

      // NOTE: We do NOT create a user message here. The extended response will appear
      // directly after the original context-based response without a duplicate query.

      // Ensure we have a session for saving the response
      const extendSessionId = await ensureSession(originalQuery, activeWorkspaceId)

      setExtendingModel(selectedLLM?.name || selectedLLM?.model || 'External Model')

      // Call the extend query API with original memory ID
      console.log('Calling API with config:', supervisorConfig)
      console.log('Summary config being sent:', summaryConfig)
      console.log('Full supervisor config being sent:', JSON.stringify(supervisorConfig, null, 2))
      if (Array.isArray(supervisorConfig)) {
        supervisorConfig.forEach((cfg, idx) => {
          console.log(`Provider[${idx}]:`, cfg.provider)
          console.log(`Model[${idx}]:`, cfg.model)
          console.log(`API Key present[${idx}]:`, !!cfg.apiKey)
        })
      } else {
        console.log('Provider:', supervisorConfig.provider)
        console.log('Model:', supervisorConfig.model)
        console.log('API Key present:', !!supervisorConfig.apiKey)
      }
      console.log('Original memory ID:', originalMemoryId)

      const response = await apiClient.extendQueryToLLM(
        originalQuery,
        supervisorConfig,
        summaryConfig,
        originalMemoryId,
        extendSessionId
      )
      console.log('API response received:', response)
      console.log('Final answer length:', response.finalAnswer?.length || 0)
      console.log('Final answer preview:', response.finalAnswer?.substring(0, 200) + '...')

      // Create assistant message with the extended response
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: response.finalAnswer,
        timestamp: new Date().toISOString(),
        metadata: {
          extendedQuery: true,
          knowledgeBased: true,
          supervisorUsed: true,
          contextRetrieved: false,
          processingTime: response.processingTime,
          agentsUsed: response.agentsUsed,
          llmProvider: response.llmProvider,
          llmModel: response.llmModel,
          originalQuery: originalQuery,
          selectedLLM: selectedLLM ? (
            selectedLLM.id === 'comparison'
              ? {
                  name: selectedLLM.name,
                  provider: 'comparison',
                  models: (selectedLLM.llms || []).map((llm) => llm.model),
                  providers: (selectedLLM.llms || []).map((llm) => llm.provider || llm.id)
                }
              : {
                  name: selectedLLM.name,
                  provider: selectedLLM.id,
                  model: selectedLLM.model
                }
          ) : null,
          updatedExisting: response.updatedExisting || false,
          memoryId: response.memoryId
        }
      }

      // Add assistant message to chat and save to session
      console.log('💬 Creating assistant message with ID:', assistantMessage.id)
      console.log('💬 Assistant message content length:', assistantMessage.content?.length || 0)
      console.log('💬 Assistant message content preview:', assistantMessage.content?.substring(0, 100) + '...')
      console.log('💬 Assistant message metadata:', assistantMessage.metadata)

      dispatch({ type: actions.ADD_CHAT_MESSAGE, payload: assistantMessage })
      setNewMessageIds(prev => new Set(prev).add(assistantMessage.id))

      // Save assistant message to session (reuse extendSessionId from above)
      const currentSessionId = extendSessionId || state.currentSessionId || await ensureSession(originalQuery, activeWorkspaceId)
      if (currentSessionId) {
        console.log('💾 Saving assistant message to session:', currentSessionId)
        await saveMessageToSession(assistantMessage, currentSessionId)
      }

      setExtendingModel(null)

      console.log('✅ Extended query completed and saved successfully!')

    } catch (error) {
      console.error('Error extending query:', error)
      setExtendingModel(null)

      // Show error message and save it
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'assistant',
        content: `**Error extending query:** ${error.message || 'An unexpected error occurred while extending the query to the LLM.'}`,
        timestamp: new Date().toISOString(),
        isError: true,
        metadata: {
          extendedQuery: true,
          error: true
        }
      }

      dispatch({ type: actions.ADD_CHAT_MESSAGE, payload: errorMessage })

      // Save error message to session
      const errorWorkspaceId = state.activeWorkspace?._id || state.activeWorkspace?.id || null
      const errorSessionId = state.currentSessionId || await ensureSession("Error extending query", errorWorkspaceId)
      if (errorSessionId) {
        await saveMessageToSession(errorMessage, errorSessionId)
      }
    }
  }

  return (
    <div className={`chat-interface ${messages.length === 0 ? 'centered-input' : ''}`}>
      <div className="messages-container">
        {messages.length === 0 ? (
          <WelcomeScreen />
        ) : (
          <div className="messages-wrapper">
            {messages.map((message, index) => {
              const isLastMessage = index === messages.length - 1
              const isCurrentlyStreaming = isTyping && isLastMessage && message.type === 'assistant'

              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isNewMessage={newMessageIds.has(message.id)}
                  onExtendQuery={handleExtendQuery}
                  isStreaming={isCurrentlyStreaming}
                  agentSteps={isCurrentlyStreaming ? agentSteps : []}
                />
              )
            })}
            {extendingModel && (
              <div className="message-wrapper assistant">
                <div className="extend-loading">
                  <span className="extend-loading-text">Consulting {extendingModel}</span>
                  <span className="extend-loading-dots">
                    <span className="extend-dot"></span>
                    <span className="extend-dot"></span>
                    <span className="extend-dot"></span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className={`input-container ${messages.length === 0 ? 'centered' : ''}`}>
        <div className="input-wrapper">
          <form className="input-form" onSubmit={handleSubmit}>
            {/* Information Sources Dropdown */}
            <div className="sources-dropdown-container" ref={dropdownRef}>
              <button
                type="button"
                className={`sources-dropdown-button ${Object.values(enabledSources).some(v => v) || personalContextEnabled ? 'active' : ''}`}
                onClick={() => setShowSourcesDropdown(!showSourcesDropdown)}
                title="Configure data sources"
              >
                <i className="fas fa-layer-group"></i>
                {(Object.values(enabledSources).filter(v => v).length + (personalContextEnabled ? 1 : 0)) > 0 && (
                  <span className="sources-count">{Object.values(enabledSources).filter(v => v).length + (personalContextEnabled ? 1 : 0)}</span>
                )}
              </button>

              {showSourcesDropdown && (
                <div className="sources-dropdown-menu">
                  <div className="sources-list">
                    {informationSources.map(source => (
                      <div key={source.id} className="source-item-simple">
                        <span className="source-name-simple">
                          {source.name}
                          {!source.isConfigured && <span className="not-configured-text"> (Not Configured)</span>}
                        </span>
                        <label className="toggle-switch-small">
                          <input
                            type="checkbox"
                            checked={!!enabledSources[source.id]}
                            onChange={() => toggleSource(source.id)}
                            disabled={!source.isConfigured}
                          />
                          <span className="toggle-slider-small"></span>
                        </label>
                      </div>
                    ))}
                  </div>
                  
                  {/* Personal Context Toggle */}
                  <div className="sources-divider"></div>
                  <div className="personal-context-toggle">
                    <div className="source-item-simple">
                      <span className="source-name-simple">
                        <i className="fas fa-user-lock" style={{ marginRight: '6px', opacity: 0.7 }}></i>
                        Personal Context
                      </span>
                      <label className="toggle-switch-small">
                        <input
                          type="checkbox"
                          checked={personalContextEnabled}
                          onChange={() => setPersonalContextEnabled(!personalContextEnabled)}
                        />
                        <span className="toggle-slider-small"></span>
                      </label>
                    </div>
                    <p className="personal-context-hint">
                      Use your imported conversation history
                    </p>
                  </div>

                  {/* Agentic Mode Toggle */}
                  <div className="sources-divider"></div>
                  <div className="agentic-mode-toggle">
                    <div className="source-item-simple">
                      <span className="source-name-simple">
                        <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '6px', opacity: 0.7 }}></i>
                        Agentic Mode
                      </span>
                      <label className="toggle-switch-small">
                        <input
                          type="checkbox"
                          checked={agenticMode}
                          onChange={() => setAgenticMode(!agenticMode)}
                        />
                        <span className="toggle-slider-small"></span>
                      </label>
                    </div>
                    <p className="personal-context-hint">
                      Trigger AI agents on each prompt
                    </p>
                  </div>
                </div>
              )}
            </div>
            <textarea
              ref={inputRef}
              className="input-field"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={loading ? "Processing..." : "Type your message here..."}
              rows="1"
              disabled={loading}
              style={{
                backgroundColor: loading ? '#f0f0f0' : '',
                opacity: loading ? 0.7 : 1
              }}
            />
            {loading && abortController ? (
              <button
                type="button"
                className="stop-button"
                onClick={handleStopResponse}
                title="Stop response"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="6"
                    y="6"
                    width="12"
                    height="12"
                    fill="currentColor"
                    rx="2"
                  />
                </svg>
                <span>Stop</span>
              </button>
            ) : (
              <button
                type="submit"
                className="send-button"
                disabled={!inputValue.trim() || loading}
                title="Send message (Enter)"
                onClick={(e) => {
                  console.log('Send button clicked:', {
                    inputValue: inputValue,
                    inputTrimmed: inputValue.trim(),
                    loading: loading,
                    disabled: !inputValue.trim() || loading,
                    buttonDisabled: e.target.disabled
                  })
                  if (!inputValue.trim()) {
                    console.warn('Button clicked but input is empty!')
                  }
                }}
              >
                {loading ? (
                  <div className="loading-spinner" />
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default ChatInterface
